import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableWithPagination from "../Tables/TableWithPagination";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTheme } from "@hooks/useTheme";
import { IMPORT_MODE_LABEL } from "@constants/index";
import queryKeys from "@configs/react-query/query-key";
import { useFetchData } from "@hooks/useFetchData";
import { formatDateTime, formatDate } from "@utils/common";
import { useMutationData } from "@hooks/useMutationData";
import { IMPORT_TYPE } from "@constants/index";
import { basename } from "../../../src/router";
import LoadingSpinner from "@components/UI/LoadingSpinner";

const ImportedDetailsModal = ({
    isOpen,
    onBack,
    onCancel,
    importStatusSeq,
    "data-testid": dataTestId = "imported-details-modal",
}) => {
    const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [tableData, setTableData] = useState([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const rowKey = "importChangelogSeq";

    const IMPORT_STATUS_CONFIG = {
        CREATED: {
            label: "preferences.imported",
            className: colors.importStatusCreated,
        },
        REJECTED: {
            label: "preferences.skipped",
            className: colors.importStatusRejected,
        },
        FAILED: {
            label: "update-summary.failed",
            className: colors.importStatusFailed,
        },
    };

    const renderStatusCell = (status) => {
        const config = IMPORT_STATUS_CONFIG[status];

        if (!config) return "-";

        return (
            <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${config.className}`}
            >
                {t(config.label)}
            </span>
        );
    };

    const noteMap = {
        "Project Code not found": "preferences.project-code-not-found",
        "Already existed": "preferences.already-existing",
        "Has no identified component or dependency": "preferences.has-no-identified-component-or-dependency",
        "Has no scans": "preferences.has-no-scans",
    };

    const renderNoteCell = (row) => {
        if (row.entity.useFlag === "N") {
            return t("preferences.this-project-has-deleted");
        }

        return t(noteMap[row.changeSummary]) || null;
    };

    const getImportStatusDetail = useFetchData(
        [queryKeys.importProjectStatusDetail, importStatusSeq],
        queryKeys.importProjectStatuses + "/" + importStatusSeq,
        { method: "GET" },
        {},
        {
            enabled: isOpen && !!importStatusSeq,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );
    const {
        data,
        isLoading,
        error
    } = getImportStatusDetail;

    const importInfo = data?.data;

    const getImportHistoryDetailData = useMutationData(
        ({ importStatusSeq }) => `/import_project_statuses/${importStatusSeq}/changelogs`,
        { method: "POST" },
        {
            onSuccess: (response) => {
                if (typeof response !== "string") {
                    setTableData(response.data || []);
                    setTotalFiles(response.total || 0);
                    setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
                }
                setLoading(false);
            },
            onError: (error) => {
                console.error("Error fetching import history:", error);
                setLoading(false);
            },
        }
    );

    useEffect(() => {
        if (isOpen && importStatusSeq) {
            setCurrentPage(1);
        }
    }, [isOpen, importStatusSeq]);

    useEffect(() => {
        if (!isOpen || !importStatusSeq) return;

        setLoading(true);

        getImportHistoryDetailData.mutate({
            importStatusSeq,
            pageSize: itemsPerPage,
            startRow: (currentPage - 1) * itemsPerPage,
        });
    }, [isOpen, importStatusSeq, currentPage, itemsPerPage]);

    const handleItemsPerPageChange = (value) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    if (!isOpen) return null;
    const isModalLoading = isLoading || getImportHistoryDetailData.isPending;

    const getImportDuration = (fromDate, toDate) => {
        if (!fromDate || !toDate) return "-";

        const from = new Date(fromDate);
        const to = new Date(toDate);

        if (to < from) return "-";

        const diffMs = to - from;

        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts = [];

        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

        return parts.join(" ");
    };

    const onProjectClick = (projectSeq) => {
        if (!projectSeq) return;

        const url = `${window.location.origin}${basename}/projects/${projectSeq}?activeTab=fossid`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const columns = ["externalId", "changeType", "changeSummary"];

    const columnConfig = {
        externalId: {
            title: t("preferences.project-id-title"),
            style: { width: "20%", textAlign: "left" },
            attributeTitle: false,
            customRender: (value, row) => {
                const isClickable = row?.entity?.useFlag === "Y";
                const displayValue = row?.changeType === "CREATED" ? row?.entity?.projectId : value;
                return (
                    <p
                        className={`text-sm font-medium
                    ${isClickable ? `${colors.textActive} cursor-pointer hover:underline` : "cursor-not-allowed opacity-50"} truncate w-full`}
                        onClick={() => {
                            if (isClickable) {
                                onProjectClick?.(row?.entity?.projectSeq);
                            }
                        }}
                    >
                        {displayValue}
                    </p>
                );
            }
        },
        changeType: {
            title: t("update-summary.status"),
            style: { width: "15%", textAlign: "left" },
            customRender: (value, row) => renderStatusCell(row.changeType),
            attributeTitle: false,
        },
        changeSummary: {
            title: t("preferences.note"),
            style: { width: "50%", textAlign: "left" },
            attributeTitle: false,
            customRender: (value, row) => renderNoteCell(row),
        },
    };

    const getImportModeDisplay = (importInfo) => {
        if (!importInfo) return "-";

        const label = t(IMPORT_MODE_LABEL[importInfo.importMode]) || "-";

        if (importInfo.importMode === IMPORT_TYPE.DATE_RANGE) {
            const from = importInfo.importFromDate
                ? formatDate(importInfo.importFromDate)
                : "-";
            const to = importInfo.importToDate
                ? formatDate(importInfo.importToDate)
                : "-";

            return `${label} (${from} → ${to})`;
        }

        return label;
    };

    return (
        <div className={`fixed inset-0 flex items-center justify-center ${colors.bgBlurModal}`} data-testid={`${dataTestId}-overlay`}>
            <div className={`w-[90vw] max-w-[1500px] max-h-[95vh] ${colors.bgModal} rounded-lg shadow-2xl border overflow-hidden flex flex-col max-h-[90vh] min-h-[50vh]`} data-testid={dataTestId}>
                {isModalLoading ? (
                    <div
                        className={`flex-1 flex items-center justify-center min-h-[50vh] ${colors.bgModal}`}
                    >
                        <LoadingSpinner
                            bgClass={colors.bgModal}
                        />
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className={`px-6 py-4 flex justify-between items-center border-b ${colors.borderMuted}`} data-testid={`${dataTestId}-header`}>
                            <div className="flex items-center gap-3">
                                <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
                                    {t("preferences.imported-details")}
                                </h2>
                                {/* <h2 className={`${colors.textPrimary} text-xl font-bold`}>
                            | {formatDateTime(importInfo?.startedAt) || "-"}
                        </h2> */}
                            </div>
                            <span
                                data-testid={fieldValueTestId("import-status")}
                                className={`px-3 py-1 rounded-full text-sm font-bold border ${importInfo?.status === "SUCCESS"
                                    ? `${colors.bgSuccessSubtle} ${colors.textSuccessBright} ${colors.borderSuccessSubtle}`
                                    : `${colors.bgFailureSubtle} ${colors.textFailureBright} ${colors.borderFailureSubtle}`
                                    }`}
                            >
                                {importInfo?.status === "SUCCESS"
                                    ? t("communication.done")
                                    : t("update-summary.failed")}
                            </span>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-8" data-testid={`${dataTestId}-content`}>
                            {/* Top Info Grid */}
                            <div className="grid grid-cols-2 gap-x-16 gap-y-6 text-sm">

                                {/* LEFT COLUMN */}
                                <div className="grid grid-cols-[180px_1fr] gap-y-4">
                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("imported-type")}>{t("preferences.imported-type")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("imported-type")}>
                                        {importInfo
                                            ? getImportModeDisplay(importInfo)
                                            : "-"}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("ignore-existing-projects")}>{t("preferences.ignore-existing-projects")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("ignore-existing-projects")}>
                                        {importInfo?.ignoreExistingProject === "Y" ? t("common.yes") : t("common.no")}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("created-at")}>{t("preferences.created-at")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("created-at")}>
                                        {formatDateTime(importInfo?.startedAt) || "-"}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("execution-time")}>{t("preferences.execution-time")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("execution-time")}>
                                        {importInfo ? getImportDuration(importInfo.startedAt, importInfo.finishedAt) : "-"}
                                    </span>
                                </div>

                                {/* RIGHT COLUMN */}
                                <div className="grid grid-cols-[180px_1fr] gap-y-4">

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("distribution-scope")}>{t("preferences.distribution-scope")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("distribution-scope")}>
                                        {importInfo?.distributionScopeCdName || "-"}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("asset-classification")}>{t("preferences.asset-classification")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("asset-classification")}>
                                        {importInfo?.assetClassification?.assetClassificationName || "-"}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("license-policy")}>{t("preferences.license-policy")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-policy")}>
                                        {importInfo?.licensePolicy?.policyName || "-"}
                                    </span>

                                    <label className={`text-sm font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("license-identifier")}>{t("license-policy.license-identifier")}</label>
                                    <span className={`text-sm ${colors.textPrimary}`} data-testid={fieldValueTestId("license-identifier")}>
                                        {importInfo?.license?.licenseIdentifier || "-"}
                                    </span>
                                </div>
                            </div>

                            {importInfo?.status === "SUCCESS" && (<div className="mb-4">
                                <div className="flex justify-between items-center">
                                    <h3 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>
                                        {t("project.project-list-title")}
                                    </h3>
                                    <span className={`${colors.bgSuccessSubtle} px-3 py-1 rounded-full text-sm border ${colors.borderSuccessSubtle} flex items-center gap-1`}>
                                        <span className={`${colors.textSuccessBright} font-medium`}>
                                            {t("preferences.improt-total")}:
                                        </span>
                                        <span className={`${colors.textSuccessBright} font-bold`}>
                                            {importInfo?.successCount} / {importInfo?.totalItems}
                                        </span>
                                    </span>
                                </div>

                                <TableWithPagination
                                    dataTestId={`${dataTestId}-project-table`}
                                    columns={columns}
                                    columnConfig={columnConfig}
                                    data={tableData}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                    loading={loading}
                                    rowKey={rowKey}
                                    totalPages={totalPages}
                                    totalData={totalFiles}
                                    maxRowsPerPage={5}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                    showPagination
                                    paddingYHeader="py-2"
                                    stickyHeader
                                />
                            </div>)}

                            {importInfo?.status !== "SUCCESS" && (
                                <div className="mb-4" data-testid={fieldValueTestId("error-message-section")}>
                                    <h3 className={`text-md font-medium ${colors.textPrimary} mb-4`}>
                                        {t("preferences.system-message")}
                                    </h3>
                                    <div className={`mt-3 rounded-lg border ${colors.borderPrimary} px-4 py-3`}>
                                        <span className={`text-sm ${colors.textDangerSubtle}`} data-testid={fieldValueTestId("error-message")}>
                                            {t("preferences.import-project-failed")}
                                        </span>
                                    </div>
                                </div>)}

                            {/* Footer */}
                            <div className={`${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
                                <div className="flex items-center justify-end">
                                    <div className="flex items-center space-x-3">
                                        <OutlineButton data-testid={`${dataTestId}-back-button`} onClick={onBack}>
                                            {t("common.back")}
                                        </OutlineButton>
                                        <OutlineButton
                                            data-testid={`${dataTestId}-cancel-button`}
                                            onClick={onCancel}
                                        >
                                            {t("common.cancel-button")}
                                        </OutlineButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ImportedDetailsModal;
